import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, DataTable, Modal, Spinner } from "@tag/tag-components-react-v4";
import { ProjectAccents } from "../../General/config/projectAccents";
import SurveyJSTemplateService from "../services/SurveyJSTemplateService";
import { SurveyJSTemplateDto } from "../types/SurveyJSTemplate";
import styles from "./SurveyJSTemplates.module.scss";

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatShortDate(value: unknown): string {
    if (typeof value !== "string" || value.length === 0) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return `${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
}

interface Props {
    visible: boolean;
    typeKey: string | null;
    typeDisplayName: string;
    isBusyByGuid: Set<string>;
    onClose: () => void;
    onActivate: (template: SurveyJSTemplateDto) => void;
    onDownload: (template: SurveyJSTemplateDto) => void;
}

function TemplateHistoryModal({ visible, typeKey, typeDisplayName, isBusyByGuid, onClose, onActivate, onDownload }: Props) {
    const [templates, setTemplates] = useState<SurveyJSTemplateDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>();

    const load = useCallback(async () => {
        if (!typeKey) return;
        setLoading(true);
        setError(undefined);
        try {
            const list = await SurveyJSTemplateService.listTemplatesByType(typeKey);
            setTemplates(list);
        } catch (e: any) {
            setError(e?.message || "Failed to load template history.");
            setTemplates([]);
        } finally {
            setLoading(false);
        }
    }, [typeKey]);

    useEffect(() => {
        if (visible && typeKey) load();
        if (!visible) setTemplates([]);
    }, [visible, typeKey, load]);

    // Refresh after an activate completes (templatesInFlight transitions off)
    useEffect(() => {
        if (visible && isBusyByGuid.size === 0) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isBusyByGuid]);

    const columns = React.useMemo(() => [
        { accessorKey: "templateName", header: "Template Name" },
        { accessorKey: "version", header: "Version" },
        {
            accessorKey: "isActive",
            header: "Active",
            cell: ({ getValue }: any) => (getValue() ? "Yes" : ""),
        },
        { accessorKey: "provisionedCount", header: "Provisioned to" },
        {
            accessorKey: "uploadedDateUtc",
            header: "Uploaded",
            cell: ({ getValue }: any) => <>{formatShortDate(getValue())}</>,
        },
        { accessorKey: "uploadedByName", header: "Uploaded by" },
        {
            id: "actions",
            header: "Actions",
            cell: ({ row }: any) => {
                const t: SurveyJSTemplateDto = row.original;
                const busy = isBusyByGuid.has(t.templateGuid);
                return (
                    <div className={styles.ActionCell}>
                        {!t.isActive && (
                            <Button
                                text={busy ? "Activating…" : "Activate"}
                                accent={ProjectAccents.CareCompliance}
                                disabled={busy}
                                onClick={() => onActivate(t)}
                            />
                        )}
                        <Button
                            text={busy ? "…" : "Download"}
                            accent={ProjectAccents.CareCompliance}
                            disabled={busy}
                            onClick={() => onDownload(t)}
                        />
                    </div>
                );
            },
        },
    ], [isBusyByGuid, onActivate, onDownload]);

    return (
        <Modal
            visible={visible}
            accent={ProjectAccents.CareCompliance}
            heading={typeDisplayName ? `Template history — ${typeDisplayName}` : "Template history"}
            primaryButtonProps={{ text: "Close", onClick: onClose }}
            cancelButtonProps={{ buttonStyle: { display: "none" } }}
        >
            <div className={styles.HistoryModalContent}>
                {error ? <Alert type="error">{error}</Alert> : null}
                {loading ? <Spinner /> : (
                    <DataTable
                        isLoading={false}
                        columns={columns}
                        data={templates}
                        accent={ProjectAccents.CareCompliance}
                        height="50vh"
                        noDataElement={
                            <div style={{ padding: 16, textAlign: "center", color: "#666" }}>
                                No templates have been uploaded for this type yet.
                            </div>
                        }
                    />
                )}
            </div>
        </Modal>
    );
}

export default TemplateHistoryModal;
